import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  Flex,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useCallbackRef } from "@mantine/hooks";

import { CoverageStatusBadge } from "@/billing/components/CoverageStatusBadge";
import { BillingTabsEnum, getClientBillingTabPath } from "@/billing/constants/billing-tabs";
import { ELIGIBILITY_REMOVE_CONFIRMATION } from "@/billing/constants/eligibility";
import useClientSidePagination from "@/billing/hooks/useClientSidePagination";
import { useEligibilityTableData } from "@/billing/hooks/useEligibilityTableData";
import {
  ELIGIBILITY_PAGE_SIZE,
  type EligibilityTableRow,
  EMPTY_CELL_DISPLAY,
  getEligibilityTableRowKey,
  isPastEligibilityCoverageEndDate,
} from "@/billing/services/eligibility-table";
import type { EligibilityAppointmentCalendarDateRange } from "@/billing/types/eligibility-appointment-date-range";
import {
  isDefaultEligibilityAppointmentCalendarDateRange,
  resolveEligibilityAppointmentCalendarDateRange,
  toEligibilityAppointmentDateRange,
} from "@/billing/utils/eligibility-appointment-date-range";
import {
  DEFAULT_ELIGIBILITY_TABLE_SORT_STATE,
  type EligibilitySortColumnKey,
  type EligibilityTableSortState,
  getNextEligibilityTableSortState,
  sortEligibilityTableRows,
} from "@/billing/utils/eligibility-table-sort";
import AnchorLink from "@/clients/components/AnchorLink";
import RemovalConfirmationModal from "@/components/RemovalConfirmationModal";
import { TextWrapper } from "@/components/TextWrapper";
import PermissionGate from "@/permission/components/PermissionGate";
import { WRITE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usePracticeToday } from "@/shared/practice-timezone/hooks";

import BillingSortableTh from "./BillingSortableTh";
import {
  ELIGIBILITY_PAYMENT_METHOD_FILTER_ALL,
  ELIGIBILITY_PAYMENT_METHOD_FILTER_OPTIONS,
  ELIGIBILITY_TABLE_COLUMNS,
} from "./constants/eligibility";
import EligibilityDateRangeControl from "./EligibilityDateRangeControl";
import RemoveFromEligibilityListButton from "./RemoveFromEligibilityListButton";

interface EligibilityTableProps {
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
}

export default function EligibilityTable({
  refreshKey = 0,
  onLoadingChange,
}: EligibilityTableProps) {
  const { practiceId } = useParams<{ practiceId: string }>();
  const { today, timeZone, isLoading: isLoadingPracticeToday } = usePracticeToday();
  // Only a custom range is kept, as the picked calendar dates. The shown window is resolved against
  // `today` on every render, so it follows the practice-local day. It is turned into instants in the
  // current practice timezone.
  const [customCalendarDateRange, setCustomCalendarDateRange] =
    useState<EligibilityAppointmentCalendarDateRange>();
  const calendarDateRange = useMemo(
    () => resolveEligibilityAppointmentCalendarDateRange(customCalendarDateRange, today),
    [customCalendarDateRange, today],
  );
  const appointmentDateRange = useMemo(
    () => toEligibilityAppointmentDateRange(calendarDateRange, timeZone),
    [calendarDateRange, timeZone],
  );
  const [appliedRefreshKey, setAppliedRefreshKey] = useState(refreshKey);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(
    ELIGIBILITY_PAYMENT_METHOD_FILTER_ALL,
  );
  const [sortState, setSortState] = useState<EligibilityTableSortState | undefined>(
    DEFAULT_ELIGIBILITY_TABLE_SORT_STATE,
  );
  const [rowToRemove, setRowToRemove] = useState<EligibilityTableRow>();
  const { rows, isLoading, error, excludeFromList } = useEligibilityTableData(
    practiceId,
    refreshKey,
    isLoadingPracticeToday ? undefined : appointmentDateRange,
  );
  const onLoadingChangeCallback = useCallbackRef(onLoadingChange);

  useEffect(() => {
    onLoadingChangeCallback?.(isLoading);
  }, [isLoading, onLoadingChangeCallback]);

  useEffect(() => {
    return () => onLoadingChangeCallback?.(false);
  }, [onLoadingChangeCallback]);

  const filteredRows = useMemo(() => {
    if (paymentMethodFilter === ELIGIBILITY_PAYMENT_METHOD_FILTER_ALL) {
      return rows;
    }
    return rows.filter((row) => row.paymentMethod === paymentMethodFilter);
  }, [rows, paymentMethodFilter]);

  const sortedRows = useMemo(
    () => sortEligibilityTableRows(filteredRows, sortState),
    [filteredRows, sortState],
  );

  const { pageRows, totalPages, displayedPage, setCurrentPage, resetToFirstPage } =
    useClientSidePagination(sortedRows, ELIGIBILITY_PAGE_SIZE);

  // A refresh reloads the whole list, so the page the user was on no longer means anything. Reset
  // during render rather than in an effect, so the first paint after a refresh is already page 1.
  if (refreshKey !== appliedRefreshKey) {
    setAppliedRefreshKey(refreshKey);
    resetToFirstPage();
  }

  const handleCalendarDateRangeChange = useCallback(
    (nextCalendarDateRange: EligibilityAppointmentCalendarDateRange) => {
      setCustomCalendarDateRange(
        isDefaultEligibilityAppointmentCalendarDateRange(nextCalendarDateRange, today)
          ? undefined
          : nextCalendarDateRange,
      );
      resetToFirstPage();
    },
    [resetToFirstPage, today],
  );

  const handlePaymentMethodFilterChange = useCallback(
    (value: string | null) => {
      setPaymentMethodFilter(value ?? ELIGIBILITY_PAYMENT_METHOD_FILTER_ALL);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const handleSort = useCallback(
    (columnKey: EligibilitySortColumnKey) => {
      setSortState((current) => getNextEligibilityTableSortState(current, columnKey));
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const handleRemoveFromList = useCallback((row: EligibilityTableRow) => {
    setRowToRemove(row);
  }, []);

  const handleCloseRemoveConfirmation = useCallback(() => {
    setRowToRemove(undefined);
  }, []);

  const handleConfirmRemoveFromList = useCallback(() => {
    if (!rowToRemove) {
      return;
    }
    void excludeFromList(
      rowToRemove.appointmentId,
      rowToRemove.clientName,
      rowToRemove.upcomingAppointment,
    );
    setRowToRemove(undefined);
  }, [excludeFromList, rowToRemove]);

  const showPaymentMethodFilter = !isLoading && !error;

  const header = (
    <Group gap="md" wrap="wrap" align="center" justify="space-between">
      <Group gap="xs">
        <Title order={2}>Eligibility Status</Title>
        {showPaymentMethodFilter ? (
          <Tooltip label="Filter by payment method">
            <Select
              clearable={false}
              value={paymentMethodFilter}
              w={{ base: "100%", sm: 240 }}
              onChange={handlePaymentMethodFilterChange}
              data-testid="eligibility-payment-method-filter"
              data={ELIGIBILITY_PAYMENT_METHOD_FILTER_OPTIONS}
            />
          </Tooltip>
        ) : null}
      </Group>
      <EligibilityDateRangeControl
        today={today}
        isDisabled={isLoadingPracticeToday}
        calendarDateRange={calendarDateRange}
        onCalendarDateRangeChange={handleCalendarDateRangeChange}
      />
    </Group>
  );

  const pager = (
    <Flex justify="center">
      <Pagination
        hideWithOnePage
        total={totalPages}
        value={displayedPage}
        onChange={setCurrentPage}
        data-testid="eligibility-pagination"
      />
    </Flex>
  );

  if (error) {
    return (
      <Stack gap="lg">
        {header}
        <Text c="red.7">Unable to load eligibility data. Please try again.</Text>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        {header}
        <Box py="xl" style={{ display: "flex", justifyContent: "center" }}>
          <Loader type="dots" />
        </Box>
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <Stack gap="lg">
        {header}
        <Text c="dimmed">No appointments in the selected date range.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {header}
      {filteredRows.length === 0 ? (
        <Text c="dimmed">No appointments match the selected payment method.</Text>
      ) : (
        <Box
          style={{
            overflow: "hidden",
            borderRadius: "var(--mantine-radius-md)",
            border: "1px solid var(--mantine-color-cream-4)",
          }}
        >
          <Table withRowBorders verticalSpacing="md" borderColor="cream.4" horizontalSpacing="md">
            <Table.Thead bg="cream.2">
              <Table.Tr>
                {ELIGIBILITY_TABLE_COLUMNS.map((column) => {
                  if (!column.sortable || column.key === "actions") {
                    return (
                      <Table.Th key={column.key} ta={column.align}>
                        <TextWrapper c="dark.9" variant="bold">
                          {column.label}
                        </TextWrapper>
                      </Table.Th>
                    );
                  }

                  return (
                    <BillingSortableTh
                      key={column.key}
                      onSort={handleSort}
                      label={column.label}
                      sortState={sortState}
                      columnKey={column.key}
                      testId={`eligibility-sort-${column.key}`}
                    />
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((row) => (
                <Table.Tr key={getEligibilityTableRowKey(row)}>
                  <Table.Td>
                    <Stack gap={0}>
                      <AnchorLink
                        c="burgundy.9"
                        to={getClientBillingTabPath(row.clientId, BillingTabsEnum.INSURANCE)}
                      >
                        {row.clientName}
                      </AnchorLink>
                      {row.medicalRecordNumber !== EMPTY_CELL_DISPLAY ? (
                        <TextWrapper size="sm" c="gray.6">
                          MRN {row.medicalRecordNumber}
                        </TextWrapper>
                      ) : null}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper>{row.upcomingAppointment}</TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper>{row.appointmentType}</TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper>{row.provider}</TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper>{row.paymentMethod}</TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper>{row.insurance}</TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper
                      c={
                        isPastEligibilityCoverageEndDate(row.coverageEndDate) ? "red.7" : undefined
                      }
                    >
                      {row.coverageEndDate}
                    </TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <TextWrapper>{row.lastVerified}</TextWrapper>
                  </Table.Td>
                  <Table.Td>
                    <CoverageStatusBadge value={row.coverageStatus} />
                  </Table.Td>
                  <Table.Td ta="right">
                    <PermissionGate
                      allowPatient={false}
                      requiredPermissions={WRITE_PERMISSION_FLAGS}
                    >
                      <RemoveFromEligibilityListButton
                        clientName={row.clientName}
                        appointmentId={row.appointmentId}
                        appointmentLabel={row.upcomingAppointment}
                        onRemoveFromList={() => handleRemoveFromList(row)}
                      />
                    </PermissionGate>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}
      {pager}
      {rowToRemove ? (
        <RemovalConfirmationModal
          isOpen
          actionText="Yes, Remove"
          itemId={rowToRemove.appointmentId}
          handleAction={handleConfirmRemoveFromList}
          handleClose={handleCloseRemoveConfirmation}
          title={ELIGIBILITY_REMOVE_CONFIRMATION.title}
          description={ELIGIBILITY_REMOVE_CONFIRMATION.description(
            rowToRemove.clientName,
            rowToRemove.upcomingAppointment,
          )}
        />
      ) : null}
    </Stack>
  );
}
